import { createModel } from '@rematch/core';
import { toast } from 'components/RevxMessage';
import { Paged } from 'domains/appDomain';
import { JobConfig } from 'domains/jobconfigDomain';
import {
  JobAdjustParams,
  JobCount,
  JobItem,
  JobRFM,
  JobStatusFilterCount,
  JobStatusFilterSeparateTabEnum,
  JobUIStatusEnum,
  JobWorkflowStatusEnum,
  JobWorkspaceFilterTypeEnum,
  QUOTE_TYPE_ENUM,
  RFMDecisionEnum,
  RFMOffer,
  SpecIdToShippingMap,
} from 'domains/workspaceDomain';
import i18n from 'i18n/config';
import { isArray, uniqBy } from 'lodash-es';
import type { RootModel } from 'models';
import { baseReducers, BaseState } from 'models/baseReducers';
import { Moment } from 'moment';
import jobconfigService from 'services/jobconfigService';
import {
  default as jobService,
  default as workspaceService,
  jobListReqCount,
} from 'services/jobService';
import { FilterOptionsParams } from 'components/RevxJobListFilter';

interface JobAdjustmentsState {
  date?: Moment;
  shippings?: SpecIdToShippingMap;
}

interface WorkspaceState extends BaseState {
  jobList: JobItem[];
  uiStatusFilter: JobUIStatusEnum;
  jobStatusCounts: JobStatusFilterCount[];
  jobItemCounts: JobCount[];
  rfmList?: JobRFM[];
  jobAdjustments: JobAdjustmentsState;
  jobListSpinning: boolean;
  size: number;
  page: number;
  invoicingTabsKey: JobStatusFilterSeparateTabEnum;
  lastUpdatedJobs: Partial<JobItem>[]; // 存储最近更新的 jobs 数据列表，用于跨页面更新
  resetJobList?: () => void;
  updateJobList?: (x: Partial<JobItem>) => void;
  reloadGroupJobList?: () => void;
}

export const workspace = createModel<RootModel>()({
  state: {
    jobList: [],
    uiStatusFilter: JobUIStatusEnum.SAVE_QUOTES,
    jobStatusCounts: [],
    jobItemCounts: [],
    rfmList: undefined,
    jobListSpinning: false,
    jobAdjustments: {},
    size: 10,
    page: 1,
    invoicingTabsKey: JobStatusFilterSeparateTabEnum.STANDARD_JOBS,
    lastUpdatedJobs: [],
  } as WorkspaceState,
  reducers: {
    ...baseReducers<WorkspaceState>(),
    updateJobItem(state: WorkspaceState, jobItem: Partial<JobItem>) {
      console.log('Updating job item:', jobItem);
      console.log('Current job list before update:', state.jobList.length);

      const index = state.jobList.findIndex((job) =>
        job.isQuote ? job.quoteId === jobItem.quoteId : job.id === jobItem.id
      );
      console.log('update index : ', index);

      // 在 lastUpdatedJobs 数组中查找是否已存在该 job 的更新
      const lastUpdatedIndex = state.lastUpdatedJobs.findIndex((job) =>
        job.isQuote ? job.quoteId === jobItem.quoteId : job.id === jobItem.id
      );

      if (lastUpdatedIndex !== -1) {
        // 如果已存在，合并更新
        state.lastUpdatedJobs[lastUpdatedIndex] = {
          ...state.lastUpdatedJobs[lastUpdatedIndex],
          ...jobItem,
        };
      } else {
        // 如果不存在，添加到数组
        state.lastUpdatedJobs.push(jobItem);
      }

      // 限制数组大小，只保留最近的 50 个更新（避免内存占用过大）
      if (state.lastUpdatedJobs.length > 50) {
        state.lastUpdatedJobs = state.lastUpdatedJobs.slice(-50);
      }

      if (index !== -1) {
        // 如果 job 在当前列表中，更新它
        state.jobList = [
          ...state.jobList.slice(0, index),
          {
            ...state.jobList[index],
            ...jobItem,
          },
          ...state.jobList.slice(index + 1),
        ];
        console.log('Job found in list and updated');
      } else {
        console.log(
          'Job not in current list, saved to lastUpdatedJobs for cross-page update'
        );
      }

      return state;
    },
    reset: (state: WorkspaceState) => {
      state.jobList = [];
      state.rfmList = undefined;
      state.jobAdjustments = {};
      return state;
    },
    setAdjustShipDate: (state, payload: Moment) => {
      state.jobAdjustments.date = payload;
      return state;
    },
    setAdjustShippings: (state, payload: SpecIdToShippingMap) => {
      state.jobAdjustments.shippings = payload;
      return state;
    },
    resetJobAdjustments: (state) => {
      state.jobAdjustments = {};
      return state;
    },
  },
  effects: (dispatch) => {
    const { workspace } = dispatch;
    return {
      async getJobList(payload: JobUIStatusEnum, state): Promise<void> {
        await dispatch.app.showLoading();
        workspace.setState({
          jobListSpinning: true,
        });
        try {
          const jobObj = await jobService.getNewJobListByStatusFilter({
            statusFilter: payload,
            page: state.workspace.page,
            size: state.workspace.size,
            filterType: JobWorkspaceFilterTypeEnum.JOB_WORKSPACE,
          });
          const jobList = jobObj.content;
          if (isArray(jobList)) {
            const jobIds = jobList.map((item) => item.id);
            if (payload !== JobUIStatusEnum.SAVE_QUOTES) {
              if (jobIds.length) {
                const jobCount = await jobService.jobCount(jobIds);
                if (isArray(jobCount) && jobCount.length) {
                  jobList.forEach((item) => {
                    const thisJobCount = jobCount.filter(
                      (jobCountItem) =>
                        String(jobCountItem.jobId) === String(item.id)
                    )[0];
                    item.notesCount = thisJobCount.jobNotesCount;
                    item.activityCount = thisJobCount.jobActivitiesCount;
                    item.allShipped = thisJobCount.allShipped;
                  });
                }
              }
            }
            if (payload === JobUIStatusEnum.RFM) {
              let rfmOffers: RFMOffer[] = [];
              if (isArray(jobIds) && jobIds.length) {
                rfmOffers = await workspaceService.latestRfmOffersByJobIds(
                  jobIds
                );
              }
              const jobRfms: JobRFM[] = rfmOffers
                .map((offer) => ({
                  job: jobList.find((job) => job.id === offer.jobId)!,
                  rfm: offer,
                }))
                .filter((jobRfm) => jobRfm.job !== undefined);
              workspace.setState({
                rfmList: jobRfms,
              });
            }
          }
          console.log('Fetched job list:', jobList);
          workspace.setState({
            jobList: jobList,
          });
        } catch {
        } finally {
          await dispatch.app.hideLoading();
          workspace.setState({
            jobListSpinning: false,
          });
        }

        if (jobListReqCount <= 0) {
          await dispatch.app.hideLoading();
          workspace.setState({
            jobListSpinning: false,
          });
        }
      },

      async getPageJobList(
        {
          page,
          size,
          filter,
          rfqClosed,
          quoteType,
          jobOrOrderNumber,
          jobName,
          supplierName,
          requesterName,
          startOrderDate,
          endOrderDate,
          statusFilterSeparateTab,
          filterType,
          excludeProjectId,
          workflowStatus,
          jobDoc,
          startDeliveryDate,
          endDeliveryDate,
          customerOrgId,
          jobId,
          salesRep,
          rfqQueryType,
        }: {
          page: number;
          size?: number;
          filterType: JobWorkspaceFilterTypeEnum;
          filter?: JobUIStatusEnum | JobWorkflowStatusEnum;
          rfqClosed?: boolean;
          quoteType?: QUOTE_TYPE_ENUM;
          statusFilterSeparateTab?: JobStatusFilterSeparateTabEnum;
          excludeProjectId?: number;
          rfqQueryType?: string;
        } & FilterOptionsParams,
        state
      ): Promise<Paged<JobItem>> {
        await dispatch.app.showLoading();
        workspace.setState({
          jobListSpinning: true,
        });
        try {
          console.log('Fetching job list with filter:', filter);
          console.log('Fetching job list with rfqQueryType:', rfqQueryType);
          const jobObj = await jobService.getNewJobListByStatusFilter({
            statusFilter:
              filter ||
              state.workspace.uiStatusFilter ||
              JobUIStatusEnum.SAVE_QUOTES,
            page: page,
            size: size || state.workspace.size,
            rfqClosed,
            quoteType,
            jobOrOrderNumber,
            jobName,
            supplierName,
            requesterName,
            startOrderDate,
            endOrderDate,
            statusFilterSeparateTab,
            filterType,
            excludeProjectId,
            workflowStatus,
            jobDoc,
            startDeliveryDate,
            endDeliveryDate,
            customerOrgId,
            jobId,
            salesRep,
            rfqQueryType,
          });
          let jobList: JobItem[] = jobObj.content;
          if (!jobList.length) {
            return jobObj;
          }
          const jobItemCounts = jobList.map((jobItem) => {
            return {
              allShipped: jobItem.allShipped,
              filesCount: jobItem.filesCount || 0,
              hasRfm: jobItem.hasRfm,
              jobActivitiesCount: jobItem.activityCount || 0,
              jobId:
                filter === JobUIStatusEnum.SAVE_QUOTES
                  ? jobItem.quoteId
                  : jobItem.id,
              jobNotesCount: jobItem.notesCount || 0,
              proofsCount: jobItem.proofsCount || 0,
            };
          });
          // console.log('Fetched job list2:', jobList);
          workspace.setState({
            jobList: jobList,
            jobItemCounts: uniqBy(
              [...jobItemCounts, ...state.workspace.jobItemCounts],
              'jobId'
            ),
          });
          return jobObj;
        } catch {
          return {
            content: [],
            pageable: {
              sort: {
                sorted: false,
                unsorted: false,
                empty: false,
              },
              offset: 0,
              pageNumber: 0,
              pageSize: 0,
              paged: false,
              unpaged: false,
            },
            totalElements: 0,
            numberOfElements: 0,
            totalPages: 0,
            size: 0,
            number: 0,
            first: false,
            empty: false,
          };
        } finally {
          workspace.setState({
            jobListSpinning: false,
          });
          await dispatch.app.hideLoading();
        }
      },
      getJobItemCount: async (id: number, state) => {
        const jobCount = await jobService.jobCount([id]);
        workspace.setState({
          jobItemCounts: uniqBy(
            [...jobCount, ...state.workspace.jobItemCounts],
            'jobId'
          ),
        });
      },
      addJobItemCount: (payload: JobCount[], state) => {
        workspace.setState({
          jobItemCounts: uniqBy(
            [...payload, ...state.workspace.jobItemCounts],
            'jobId'
          ),
        });
      },
      reloadJobList: (payload: void, state) => {
        if (typeof state.workspace.resetJobList === 'function') {
          state.workspace.resetJobList();
        }
      },
      reloadGroupJobList: (payload: void, state) => {
        if (typeof state.workspace.reloadGroupJobList === 'function') {
          state.workspace.reloadGroupJobList();
        }
      },
      updateJobPageItem: (payload: Partial<JobItem>, state) => {
        if (typeof state.workspace.updateJobList === 'function') {
          console.log('Updating job list with payload:', payload);
          state.workspace.updateJobList(payload);
        } else {
          console.log('No updateJobList function defined in state.');
        }
      },
      setJobList: (jobList: JobItem[], state) => {
        workspace.setState({ jobList });
      },
      async updateJob(payload: Partial<JobItem>) {
        let item: JobItem;
        let jobConfig: JobConfig;
        workspace.updateJobPageItem({ ...payload, name: payload!.name });
        try {
          if (payload.isQuote && payload.quoteId) {
            jobConfig = await jobconfigService.basicUpdate(payload.quoteId, {
              projectName: payload.name!,
            });
          } else {
            item = await jobService.updateJob(payload);
          }
        } catch (e) {
          return;
        }
        workspace.updateJobPageItem(
          payload.isQuote
            ? {
                ...payload,
                name: jobConfig!.projectName,
                jobSharedWiths: jobConfig!.jobSharedWiths,
              }
            : item!
        );
      },

      async getJobDetail(jobId: number, state): Promise<JobItem> {
        dispatch.app.showLoading();
        const res = await jobService.getJobDetail(jobId);
        const oldItem = state.workspace.jobList.find(
          (item) => item.id === res.id
        );
        workspace.updateJobPageItem({
          ...oldItem,
          ...res,
        });
        dispatch.app.hideLoading();
        return res;
      },

      async getJobCountByStatusFilter(payload: JobUIStatusEnum[]) {
        try {
          if (payload.length === 1 && payload[0] === JobUIStatusEnum.RFQ) {
            return;
          }
          const jobStatusCounts =
            await workspaceService.getJobCountByStatusFilter(
              payload.filter((s) => s !== JobUIStatusEnum.SEPARATOR)
            );
          workspace.setState({
            jobStatusCounts,
          });
        } catch {}
      },

      setUiStatusFilter(uiStatusFilter: JobUIStatusEnum) {
        workspace.setState({
          uiStatusFilter,
        });
        localStorage.setItem('uiStatusFilter', uiStatusFilter);
      },

      async reorder(jobId: number): Promise<JobConfig | null> {
        dispatch.app.showLoading();
        try {
          const res = await workspaceService.reorder(jobId);
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return null;
        }
      },

      async declineRFMInvitation(payload: number, state): Promise<void> {
        dispatch.app.showLoading();
        try {
          await jobService.decideOnInvitation(
            payload,
            RFMDecisionEnum.DECLINED
          );
          dispatch.app.hideLoading();
        } catch {
          dispatch.app.hideLoading();
        }
      },

      async acceptRFMInvitation(payload: number, state): Promise<void> {
        dispatch.app.showLoading();
        try {
          await jobService.decideOnInvitation(
            payload,
            RFMDecisionEnum.ACCEPTED
          );
          toast(i18n.t('toast.supplierRFMAcceptSuccess'));
          dispatch.app.hideLoading();
        } catch {
          toast(i18n.t('toast.supplierRFMHasAcceptedError'));
          dispatch.app.hideLoading();
        }
      },

      async adjustJob(payload: JobAdjustParams, state): Promise<void> {
        dispatch.app.showLoading();
        try {
          await jobService.adjustJob(payload);
          dispatch.app.hideLoading();
        } catch {
          dispatch.app.hideLoading();
        }
      },
    };
  },
});
